<script lang="ts">
  import { t } from '$lib/i18n';
  import { drawer } from '$lib/stores/drawer';
  import Button from '$components/ui/button.svelte';
  import Icon from '$components/ui/icon/icon.svelte';
  import Drawer from '$components/drawer.svelte';
  import ReviewsForm from './reviews-form.svelte';
  import ReviewsPanel from './reviews-panel.svelte';
  import { createQuery } from '@tanstack/svelte-query';
  import { getReviewsData, type Reviews } from '$lib/yotpo';
  import Skeleton from '$components/ui/skeleton.svelte';
  import { onMount } from 'svelte';
  import { page } from '$app/stores';
  import YotpoReviews from '$components/blocks/yotpo-reviews.svelte';
  import { analyticsEvent, AnalyticsEvents } from '$lib/analytics';
  export let product: {
    handle: string;
    id: string;
    title: string;
    category: string;
    price: number;
    currency: string;
    image: string;
    productType: string;
    variant: string;
  };

  $: averageScore = 0;

  $: client = createQuery<Reviews>({
    staleTime: Infinity,
    queryKey: ['reviews', product.id],
    queryFn: async ({ pageParam = 1 }) => {
      return await getReviewsData({
        id: product.id,
        page: pageParam,
        star: undefined,
        sort: 'default'
      });
    }
  });

  $: if (!$client.isLoading) {
    averageScore = Number(($client.data?.bottomline?.average_score || 0)?.toFixed(2));
  }

  const getStars = (score: number) => {
    const roundedScore = Math.round(score * 2) / 2; // Round to nearest 0.5
    const fullStars = Math.round(roundedScore); // This will round .5 up to a full star
    const emptyStars = 5 - fullStars;

    return { full: Array(fullStars).fill('★'), empty: Array(emptyStars).fill('☆') };
  };

  $: stars = getStars(averageScore);

  $: totalReviews = $client.data?.bottomline?.total_review || 0;
  $: topReviews = $client.data?.reviews?.slice(0, 2);

  onMount(() => {
    if ($page.url.searchParams.get('review')) {
      setTimeout(() => drawer.open('review'));
      // remove param
      window.history.replaceState({}, '', window.location.pathname);
    }
  });

  const handleOpenReview = () => {
    analyticsEvent({
      event: AnalyticsEvents.OPEN_REVIEWS,
      items: [
        {
          item_id: product.handle,
          item_name: product.title,
          item_category: product.productType,
          item_variant: product.variant
        }
      ]
    });
    drawer.open(`review-${product.id}`, {
      meta: {
        showCartButtonOnMobile: true,
        titleToShowOnMobileNavigation: $t('reviews.title')
      }
    });
  };
</script>

{#if $client.isLoading}
  <div class="flex items-center justify-between">
    <Skeleton class="h-64 w-full rounded-md" />
  </div>
{:else if totalReviews && topReviews}
  <div
    class="flex p-3 flex-col flex-start gap-3 align-self-stretch border-primary/4 border bg-primary/2 rounded-sm"
  >
    <div class="flex flex-row justify-between items-center">
      <span class="text-sm text-primary">Reviews ({totalReviews})</span>
      <Button
        variant="ghost"
        size="xs"
        name="open-review"
        icon="chevron-right"
        class="border-none h-0"
        on:click={() => handleOpenReview()}
      />
    </div>
    <div class="flex flex-row gap-2 items-center">
      <p class="text-lg font-semibold">{averageScore}</p>
      <div class="flex flex-row items-center">
        {#each stars.full as _}
          <Icon size="sm" icon="star-filled" class="text-gold" />
        {/each}
        {#each stars.empty as _}
          <Icon size="sm" icon="star-filled" class="text-gold/20" />
        {/each}
      </div>
    </div>
    {#if $client.data?.reviews}
      <div class="flex flex-row gap-2 overflow-x-auto hide-scrollbar h-fit">
        {#each $client.data.reviews.slice(0, 6) as review}
          <YotpoReviews
            reviews={[review]}
            variant="small"
            class="shrink-0"
            productId={product.id}
            productTitle={product.title}
            productType={product.productType}
            productVariant={product.variant}
          />
        {/each}
      </div>
    {/if}
  </div>
{:else}
  <button
    class="relative overflow-hidden border-primary/4 border bg-primary/2 rounded-sm w-full"
    name="open-review"
    on:click={() => {
      drawer.open('review');
    }}
  >
    <div class="flex w-full items-center justify-between p-3 text-sm">
      <span class="text-medium hover:opacity-80 transition-all flex items-center gap-2 text-left">
        <Icon size="sm" icon="star-filled" class="text-gold" />
        {$t('reviews.no-reviews.button')}
      </span>
      <Icon size="sm" icon="chevron-right" />
    </div>
  </button>
{/if}

<!-- Drawer -->
<Drawer id={`review-${product.id}`} title={$t('reviews.title')}>
  <ReviewsPanel
    productId={product.id}
    productTitle={product.title}
    productType={product.productType}
    productVariant={product.variant}
  />
</Drawer>

<Drawer id="review" title={$t('reviews.post')}>
  <ReviewsForm {product} />
</Drawer>
